import { type ColumnData } from '@frontend/api';

import { type ColumnState } from './types';

const ARRAY_SEPARATOR = ',';
const COLUMN_PARAMETER_KEY = 'columns';

export const ColumnTransformer = {
  /**
   * Given a list of query param names you want to display, generate a state value
   * that the VisibleColumnsConfig can interpret. Which is an ordered list of
   * ColumnState objects, respecting the order of the queryParamNames passed in
   *
   * NOTE: Assumes children will only be one level deep
   *
   * @param columnConfigData: column data from the column config endpoint
   * @param queryParamNames: flat list of string query param names you want to display
   */
  transformDisplayQueryParamNamesToVisibleColumnConfigState: (
    queryParamNames: string[],
    columnConfigData: ColumnData[], // nested column data
  ): ColumnState[] => {
    const configMap: Record<string, ColumnData> = {};
    const childrenKeys: Set<string> = new Set();
    const childKeyToParentKey: Record<string, string> = {};
    const topLevelKeys: string[] = [];
    for (const column of columnConfigData) {
      topLevelKeys.push(column.queryParamName);
      configMap[column.queryParamName] = column;
      for (const childColumn of column.children ?? []) {
        configMap[childColumn.queryParamName] = childColumn;
        childrenKeys.add(childColumn.queryParamName);
        childKeyToParentKey[childColumn.queryParamName] = column.queryParamName;
      }
    }

    const seenQueryParamNames: Set<string> = new Set();
    const out: ColumnState[] = [];
    for (const queryParamName of queryParamNames) {
      if (seenQueryParamNames.has(queryParamName)) {
        // NOTE: we could hit this condition when processing keys that are
        // children
        continue;
      }

      if (childrenKeys.has(queryParamName)) {
        // build all of the parent's child entries now for organization
        // NOTE: assuming ordering of the keys does not matter for child entities
        const parentKey = childKeyToParentKey[queryParamName];
        const hasKey = parentKey in configMap;
        if (!hasKey) continue;

        const parentColumn = configMap[parentKey];
        const parentColumnState: ColumnState = {
          ...parentColumn,
          isVisible: false,
          children: [], // will be filled in below
        };
        for (const childColumn of parentColumn.children ?? []) {
          seenQueryParamNames.add(childColumn.queryParamName);
          parentColumnState.children?.push({
            ...childColumn,
            isVisible: queryParamNames.includes(childColumn.queryParamName),
          });
        }
        seenQueryParamNames.add(parentKey);
        out.push(parentColumnState);
      } else {
        const hasKey = queryParamName in configMap;
        if (!hasKey) continue;

        const column = configMap[queryParamName];
        seenQueryParamNames.add(column.queryParamName);
        out.push({
          ...column,
          isVisible: true,
        });
      }
    }

    // now append all the unused columns to the bottom of the list in order
    // of how they appear in the column config
    for (const topLevelKey of topLevelKeys) {
      if (!seenQueryParamNames.has(topLevelKey)) {
        const hasKey = topLevelKey in configMap;
        if (!hasKey) continue;

        const column = configMap[topLevelKey];
        const entry: ColumnState = {
          ...column,
          isVisible: false,
        };
        if (entry.children) {
          const nextChildren: ColumnState[] = [];
          for (const childColumn of entry.children) {
            nextChildren.push({
              ...childColumn,
              isVisible: false,
            });
          }
          entry.children = nextChildren;
        }
        out.push(entry);
      }
    }
    return out;
  },

  transformColumnStateToSearch: (
    columns: ColumnState[], // flattened list
    currentSearch: URLSearchParams,
    availableColumns: ColumnData[],
  ): URLSearchParams => {
    const newSearch = new URLSearchParams(currentSearch);
    const validQueryParamNames = availableColumns.map(
      (col) => col.queryParamName,
    );
    const validColumns = columns.filter(({ queryParamName }) =>
      validQueryParamNames.includes(queryParamName),
    );

    if (validColumns.length === 0) {
      newSearch.delete(COLUMN_PARAMETER_KEY);
    } else {
      const rawColumnsString = validColumns
        .reduce((acc, c) => {
          // checks to see if the isVisible prop exists, if so, use it, if not
          // defer to default visibility
          if (c.isVisible ?? c.defaultVisible) {
            acc.push(c.queryParamName);
          }

          return acc;
        }, [] as string[])
        .join(ARRAY_SEPARATOR);
      // NOTE: we are intentionally not encoding here, it causes an issue
      newSearch.set(COLUMN_PARAMETER_KEY, rawColumnsString);
    }
    return newSearch;
  },

  transformNestedColumnStateToFlatColumnState: (
    availableColumns: ColumnData[],
    submitValues: ColumnState[],
  ): ColumnState[] => {
    // NOTE: Assumes children only ever one level deep and the parent element
    //  should not be included
    const out: ColumnState[] = [];
    for (const columnVal of submitValues) {
      if (columnVal.children) {
        for (const childColumnVal of columnVal.children ?? []) {
          out.push(childColumnVal);
        }
      } else {
        out.push(columnVal);
      }
    }
    return out;
  },

  transformInternalColumnStateToQueryParams: (
    displayedColumns: ColumnData[],
  ): string[] => {
    return displayedColumns.map((column) => column.queryParamName);
  },
};
